<template>
  <div
    v-click-outside="onClose"
    :class="['dropdown', { 'is-active': isActivated }]"
  >
    <div class="dropdown-trigger" @click="disableToggle ? {} : toggle()">
      <slot />
    </div>
    <div ref="dropdownMenu" class="dropdown-menu">
      <div class="dropdown-content">
        <slot name="content" :close="onClose" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance, onMounted, ref } from 'vue'
import vClickOutside from '@sephora-asia/ui-click-outside/ui-click-outside'
import { ContentOverflow } from './types'

interface Props {
  boundary?: string
  disableToggle?: boolean
  contentOverflow?: ContentOverflow
}

const props = withDefaults(defineProps<Props>(), {
  boundary: '',
  disableToggle: false,
  contentOverflow: ContentOverflow.RIGHT,
})
const emit = defineEmits<{
  (e: 'close'): void
}>()

// Two-way binding for `isActivated` which can be controlled by the parent component as well
const isActivated = defineModel<boolean>({
  default: false,
})

onMounted(() => {
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      onClose()
    }
  })
  if (props.boundary) {
    reposition()
  }
})

const dropdownMenu = ref<HTMLDivElement | undefined>()

function toggle(): void {
  isActivated.value = !isActivated.value
}

function onClose(): void {
  isActivated.value = false
  emit('close')
}

function getBoundingElement(node: any): HTMLElement | undefined {
  const boundingElement = node.refs[props.boundary]
  if (boundingElement) {
    return boundingElement
  } else if (node.parent) {
    return getBoundingElement(node.parent)
  } else {
    console.error('[Error] Ref not found: ', props.boundary)
  }
}

function reposition(): void {
  // BoundingElement is the parent of this dropdown.
  // Visually, it is the box that will contain the dropdown trigger and its content
  const componentInstance = getCurrentInstance()
  const boundingElement: HTMLElement | undefined = getBoundingElement(
    componentInstance?.parent
  )
  if (boundingElement) {
    // Case: When dropdown content will overflow towards right.
    // We don't want dropdown content to go beyond the bounding element's right side.
    // However, it is possible that content will overflow on the left side when it's wider than boundingElement
    const content: unknown | any = componentInstance?.refs?.dropdownMenu
    if (props.contentOverflow === ContentOverflow.RIGHT) {
      // This is the boundingElement `right` attribute
      const boundaryRight = boundingElement.getBoundingClientRect().right
      // This is the content `right` attribute
      const contentRight = content.getBoundingClientRect().right
      // This is length wherein the content overflows beyond the boundingElement
      // Negative value means that it has overflowed
      const overflowWidth = boundaryRight - contentRight
      if (overflowWidth < 0) {
        // Content `left` attribute is 0 by default
        // Adding a negative value will move it further to the left side. Therefore, setting
        // the overflowWidth as the value will offset the overflow to the left side instead
        // of the right side
        // After setting the `left` attribute to the overflowWidth, the difference between
        // boundaryRight and contentRight will be 0.
        content.style.left = `${overflowWidth}px`
      }
    } else {
      // Case: When dropdown content will overflow towards left.
      // It can't go beyond boundingElement's left. However, it is possible that
      // content will overflow on the right side when it's wider than boundingElement.
      const boundaryLeft = boundingElement.getBoundingClientRect().left
      const contentLeft = content.getBoundingClientRect().left
      const overflowWidth = boundaryLeft - contentLeft
      if (overflowWidth < 0) {
        content.style.left = `${overflowWidth}px`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dropdown {
  display: inline-flex;
  position: relative;
  vertical-align: top;
}

.dropdown-menu {
  padding-top: 4px;
  position: absolute;
  top: 100%;
  z-index: var(--size-z-index-toast);
}

.dropdown-content {
  @extend .background-shadow;
  background-color: var(--color-background-base);
  border-radius: var(--size-radius-medium);
}

.dropdown-menu {
  display: block;
  visibility: hidden;
}

.dropdown.is-active,
.dropdown.is-hoverable:hover {
  .dropdown-menu {
    visibility: visible;
  }
}

.dropdown.is-full-width {
  display: flex;

  .dropdown-trigger,
  .dropdown-menu {
    width: 100%;
  }
}
</style>
